<script>
  import {
    registrationSection,
    resolveProductMeta,
    resolveProductSlug,
    TICKET_QTY_MIN,
    TICKET_QTY_MAX
  } from './registration.js';
  import { precalculateOrder } from '../services/gigmaApi.js';
  import {
    values,
    checks,
    errors,
    formError,
    isSubmitting,
    setField,
    toggleCheck,
    submitForm,
    promoCode,
    promoStatus,
    promoInfo,
    promoError,
    isApplyingPromo,
    setPromoCode,
    applyPromoCode,
    clearPromoCode,
    orderAlreadyCreated,
    resetOrderGuard,
    ticketQty,
    setTicketQty
  } from '../stores/registration.js';

  export let productId;
  export let productSlug = 'basic';

  // Динамическая цена: precalculate дёргается ВСЕГДА (для known и unknown),
  // чтобы UI и YooKassa-сумма всегда совпадали.
  //
  // Цены кэшируются в map по productId, не в одной переменной. На смену id
  // effectiveUnitPrice мгновенно переключается на цену соответствующего
  // продукта (или productMeta.price-хардкод как fallback для known). Без
  // map при SPA-back/forward между разными `?product=...` URL дисплеилась
  // бы цена от прошлого продукта, пока летит запрос (P2 Codex).
  let dynamicUnitPrices = {}; // { [productId]: number }
  let dynamicLoading = false;
  let loadedForId = null;

  $: productMeta = resolveProductMeta(productSlug);
  $: isKnownProduct = !!resolveProductSlug(productId);
  // Приоритет: Gigma-цена для ТЕКУЩЕГО id → хардкод (только для known) → null.
  $: effectiveUnitPrice =
    productId != null && dynamicUnitPrices[productId] != null
      ? dynamicUnitPrices[productId]
      : isKnownProduct
        ? productMeta.price
        : null;
  $: subtotal = effectiveUnitPrice != null ? effectiveUnitPrice * $ticketQty : null;
  // Когда промо применён — итог берём из ответа precalculate (там уже учтено qty).
  // Иначе считаем сами: unit × qty (если знаем unit).
  $: displayTotal = ($promoStatus === 'applied' && $promoInfo) ? $promoInfo.finalPrice : subtotal;
  $: displayOriginal = ($promoStatus === 'applied' && $promoInfo && $promoInfo.originalPrice > $promoInfo.finalPrice)
    ? $promoInfo.originalPrice
    : null;

  // Реактивно: на смену productId — дёргаем precalculate, если ещё не кэшировали.
  $: maybeFetchDynamicPrice(productId);

  async function maybeFetchDynamicPrice(id) {
    if (!id) return;
    if (dynamicUnitPrices[id] != null) return; // уже знаем цену
    if (loadedForId === id) return; // запрос уже в полёте
    loadedForId = id;
    dynamicLoading = true;
    try {
      const data = await precalculateOrder(id, null, 1);
      const price = Number(data?.final_price);
      if (Number.isFinite(price)) {
        // Реактивное обновление: новый объект, чтобы Svelte увидел change.
        dynamicUnitPrices = { ...dynamicUnitPrices, [id]: price };
      }
    } catch (e) {
      // Молча: для known остаётся productMeta.price (хардкод), для unknown —
      // «Цена будет указана при оплате». createOrder всё ещё работает.
    } finally {
      if (loadedForId === id) dynamicLoading = false;
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    submitForm();
  }

  function onPromoInput(event) {
    setPromoCode(event.currentTarget.value);
  }

  function onPromoApply() {
    if (!productId) return;
    applyPromoCode(productId);
  }

  function changeQty(delta) {
    const next = Math.max(TICKET_QTY_MIN, Math.min(TICKET_QTY_MAX, $ticketQty + delta));
    if (next === $ticketQty) return;
    setTicketQty(next);
    // Если промокод применён, пересчитываем итог под новое количество.
    if ($promoStatus === 'applied' && productId) {
      applyPromoCode(productId);
    }
  }

  function formatPrice(value) {
    if (!Number.isFinite(value)) return '';
    return new Intl.NumberFormat('ru-RU').format(value) + ' ₽';
  }
</script>

<section class="registration-section" aria-labelledby="registration-title">
  <div class="back-row">
    <a class="back-link" href={registrationSection.back.href}>
      <span class="back-icon" aria-hidden="true"></span>
      <span>{registrationSection.back.label}</span>
    </a>
  </div>

  <div class="registration-frame">
    <div class="registration-content">
      <header class="registration-head">
        <h1 id="registration-title">{registrationSection.title}</h1>
        <p class="registration-note">{registrationSection.note}</p>
      </header>

      {#if $orderAlreadyCreated}
        <div class="already-ordered" role="status">
          <h2>{registrationSection.alreadyOrdered.title}</h2>
          <p>{registrationSection.alreadyOrdered.description}</p>
          <div class="already-actions">
            <a class="already-cta primary" href={registrationSection.alreadyOrdered.accountCta.href}>
              <span>{registrationSection.alreadyOrdered.accountCta.label}</span>
            </a>
            <button type="button" class="already-cta ghost" on:click={resetOrderGuard}>
              <span>{registrationSection.alreadyOrdered.newOrderCta.label}</span>
            </button>
          </div>
        </div>
      {:else}
      <form class="registration-form" novalidate on:submit={handleSubmit}>
        <div class="field-list">
          {#each registrationSection.fields as field}
            <label class="field-group">
              <input
                class="input"
                class:has-error={$errors[field.name]}
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                autocomplete={field.autocomplete}
                value={$values[field.name]}
                on:input={(e) => setField(field.name, e.currentTarget.value)}
              />
              {#if field.hint}
                <span class="hint">
                  {field.hint}
                  {#if field.hintLink}
                    <a href={field.hintLink.href}>{field.hintLink.label}</a>
                  {/if}
                </span>
              {/if}
              {#if $errors[field.name]}
                <span class="error">{$errors[field.name]}</span>
              {/if}
            </label>
          {/each}
        </div>

        <div class="order-summary" aria-label="Ваш заказ">
          <div class="order-summary-row">
            <div class="order-summary-tariff">
              <span class="order-summary-label">Тариф</span>
              <span class="order-summary-tariff-name">«{isKnownProduct ? productMeta.label : 'Билет на форум'}»</span>
              {#if effectiveUnitPrice != null}
                <span class="order-summary-unit">{formatPrice(effectiveUnitPrice)}</span>
              {:else if dynamicLoading}
                <span class="order-summary-unit">Загружаем цену…</span>
              {:else}
                <span class="order-summary-unit">Цена будет указана при оплате</span>
              {/if}
            </div>
            <div class="order-summary-qty">
              <span class="order-summary-label">Количество</span>
              <div class="qty-stepper" role="group" aria-label="Количество билетов">
                <button
                  type="button"
                  class="qty-btn"
                  aria-label="Убрать билет"
                  on:click={() => changeQty(-1)}
                  disabled={$ticketQty <= TICKET_QTY_MIN || $isSubmitting || $isApplyingPromo}
                >−</button>
                <span class="qty-value" aria-live="polite">{$ticketQty}</span>
                <button
                  type="button"
                  class="qty-btn"
                  aria-label="Добавить билет"
                  on:click={() => changeQty(1)}
                  disabled={$ticketQty >= TICKET_QTY_MAX || $isSubmitting || $isApplyingPromo}
                >+</button>
              </div>
            </div>
          </div>
          <div class="order-summary-total">
            <span class="order-summary-label">Итого к оплате</span>
            <span class="order-summary-total-value">
              {displayTotal != null ? formatPrice(displayTotal) : '—'}
              {#if displayOriginal}
                <span class="order-summary-strike">{formatPrice(displayOriginal)}</span>
              {/if}
            </span>
          </div>
        </div>

        <div class="promo-group">
          <label class="promo-label" for="promo-code">Промокод</label>
          <div class="promo-row">
            <input
              id="promo-code"
              class="input promo-input"
              type="text"
              autocomplete="off"
              placeholder={registrationSection.promo.placeholder}
              value={$promoCode}
              pattern={registrationSection.promo.pattern}
              on:input={onPromoInput}
              disabled={$promoStatus === 'applied' || $isApplyingPromo}
            />
            {#if $promoStatus === 'applied'}
              <button
                type="button"
                class="promo-btn promo-clear"
                on:click={clearPromoCode}
                disabled={$isApplyingPromo || $isSubmitting}
              >
                <span>{registrationSection.promo.clear}</span>
              </button>
            {:else}
              <button
                type="button"
                class="promo-btn"
                on:click={onPromoApply}
                disabled={$isApplyingPromo || !$promoCode.trim()}
              >
                <span>{$isApplyingPromo ? '...' : registrationSection.promo.apply}</span>
              </button>
            {/if}
          </div>

          {#if $promoStatus === 'applied' && $promoInfo}
            <p class="promo-status promo-ok">
              {registrationSection.promo.applied}{$promoInfo.name ? ` — ${$promoInfo.name}` : ''}.
            </p>
          {:else if $promoStatus === 'invalid' && $promoError}
            <p class="promo-status promo-bad">{$promoError}</p>
          {/if}
        </div>

        <div class="agreements">
          {#each registrationSection.agreements as agreement}
            <label class="agreement">
              <input
                class="check"
                type="checkbox"
                checked={$checks[agreement.name]}
                on:change={() => toggleCheck(agreement.name)}
              />
              <span>
                {#if agreement.prefix}
                  {agreement.prefix}
                {/if}
                {#if agreement.link}
                  <a href={agreement.link.href} target="_blank" rel="noreferrer">{agreement.link.text}</a>
                {:else}
                  {agreement.label}
                {/if}
              </span>
            </label>
          {/each}
        </div>

        {#if $formError}
          <p class="form-error" role="alert">{$formError}</p>
        {/if}

        <div class="form-actions">
          <button class="submit" type="submit" disabled={$isSubmitting}>
            <span>{$isSubmitting ? 'Отправка...' : registrationSection.submit.label}</span>
            <span class="arrow" aria-hidden="true"></span>
          </button>
        </div>

        <p class="help-fallback">
          Возникли проблемы с оплатой или регистрацией?
          <a href="https://max.ru/u/f9LHodD0cOJ1otgVx9KGa4maaCIVqQH5TX7X3JwN8a8incQQs69OylZpCCU" target="_blank" rel="noreferrer">
            Написать организатору
          </a>
        </p>
      </form>
      {/if}
    </div>
  </div>
</section>

<style>
  .registration-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 100px 64px;
    background: #000000;
    color: #ffffff;
    box-sizing: border-box;
  }

  .back-row {
    width: 100%;
    max-width: 1288px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 60px;
    padding-block: 6px;
    color: #ffffff;
    font-family: Manrope, Roboto, Arial, sans-serif;
    font-size: 16px;
    font-weight: 500;
    text-decoration: none;
    transition: color 160ms ease;
  }

  .back-link:hover,
  .back-link:focus-visible {
    color: #e7b962;
  }

  .back-icon {
    position: relative;
    width: 20px;
    height: 20px;
  }

  .back-icon::before {
    content: '';
    position: absolute;
    inset: 4px 7px 4px 6px;
    background: currentColor;
    -webkit-mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
    mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
    transform: rotate(180deg);
  }

  .registration-frame {
    width: 100%;
    max-width: 1440px;
    display: flex;
    justify-content: center;
    padding: 40px 64px;
    box-sizing: border-box;
  }

  .registration-content {
    display: flex;
    width: 100%;
    max-width: 1288px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding-inline: 12px;
    box-sizing: border-box;
  }

  .registration-head {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }

  h1 {
    margin: 0;
    width: 100%;
    color: #ffffff;
    font-size: 56px;
    font-weight: 700;
    line-height: 56px;
  }

  .registration-note {
    margin: 0;
    width: 100%;
    padding: 32px 30px 48px 0;
    color: rgba(255, 255, 255, 0.5);
    font-size: 24px;
    font-weight: 400;
    line-height: 36px;
  }

  .already-ordered {
    width: 620px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 32px;
    border: 1px solid rgba(231, 185, 98, 0.5);
    border-radius: 16px;
    background: rgba(231, 185, 98, 0.06);
  }

  .already-ordered h2 {
    margin: 0;
    color: #ffffff;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
  }

  .already-ordered p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    line-height: 24px;
  }

  .already-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 8px;
  }

  .already-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 13px 23px;
    border-radius: 5px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;
  }

  .already-cta.primary {
    border: 0;
    background: #e7b962;
    color: #000000;
  }

  .already-cta.primary:hover,
  .already-cta.primary:focus-visible {
    background: #ffa800;
  }

  .already-cta.ghost {
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
  }

  .already-cta.ghost:hover,
  .already-cta.ghost:focus-visible {
    border-color: #ffa800;
    color: #ffffff;
    background: rgba(255, 168, 0, 0.05);
  }

  @media (max-width: 767px) {
    .already-ordered {
      padding: 24px 20px;
    }

    .already-ordered h2 {
      font-size: 22px;
    }

    .already-cta {
      width: 100%;
    }
  }

  .registration-form {
    width: 620px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .field-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .field-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .input {
    width: 100%;
    height: 51px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    background: transparent;
    padding: 14px 16px;
    color: #ffffff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
    outline: none;
    box-sizing: border-box;
    transition: border-color 160ms ease;
  }

  .input::placeholder {
    color: rgba(255, 255, 255, 0.4);
    opacity: 1;
  }

  .input:focus {
    border-color: #ffa800;
  }

  .input.has-error {
    border-color: #ff6b6b;
  }

  .hint {
    margin: 0;
    width: 100%;
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    font-weight: 400;
    line-height: normal;
  }

  .hint a {
    color: #ffa800;
  }

  .error {
    color: #ff6b6b;
    font-size: 12px;
  }

  .agreements {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 48px 30px 0 0;
  }

  .agreement {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #ffffff;
    font-size: 18px;
    line-height: normal;
    cursor: pointer;
  }

  .agreement a {
    color: #ffa800;
  }

  .check {
    position: relative;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    appearance: none;
    margin: 0;
    border: 2px solid rgba(255, 168, 0, 0.5);
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    transition: border-color 160ms ease;
  }

  .check:checked {
    border-color: #ffa800;
  }

  .check:checked::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 10px;
    height: 7px;
    border-left: 2px solid #ffa800;
    border-bottom: 2px solid #ffa800;
    transform: translate(-50%, -65%) rotate(-45deg);
  }

  .form-error {
    margin: 24px 0 0;
    color: #ff6b6b;
    font-size: 14px;
  }

  .order-summary {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 30px;
    margin-top: 24px;
    border: 1px solid rgba(231, 185, 98, 0.35);
    border-radius: 16px;
    background: rgba(231, 185, 98, 0.04);
    box-sizing: border-box;
  }

  .order-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }

  .order-summary-tariff,
  .order-summary-qty {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .order-summary-label {
    color: rgba(255, 255, 255, 0.55);
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }

  .order-summary-tariff-name {
    color: #ffffff;
    font-size: 20px;
    font-weight: 500;
  }

  .order-summary-unit {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }

  .qty-stepper {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    overflow: hidden;
    width: fit-content;
  }

  .qty-btn {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: #ffffff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;
  }

  .qty-btn:not(:disabled):hover,
  .qty-btn:not(:disabled):focus-visible {
    background: rgba(231, 185, 98, 0.16);
    color: #e7b962;
  }

  .qty-btn:disabled {
    color: rgba(255, 255, 255, 0.25);
    cursor: not-allowed;
  }

  .qty-value {
    min-width: 32px;
    text-align: center;
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    user-select: none;
  }

  .order-summary-total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .order-summary-total-value {
    color: #e7b962;
    font-size: 26px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .order-summary-strike {
    margin-left: 10px;
    color: rgba(255, 255, 255, 0.45);
    font-size: 16px;
    font-weight: 400;
    text-decoration: line-through;
  }

  @media (max-width: 767px) {
    .order-summary {
      padding: 20px 18px;
    }
    .order-summary-row {
      gap: 16px;
    }
    .order-summary-tariff-name {
      font-size: 18px;
    }
    .order-summary-total-value {
      font-size: 22px;
    }
  }

  .promo-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 32px 30px 0 0;
  }

  .promo-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: 500;
  }

  .promo-row {
    display: flex;
    gap: 12px;
    align-items: stretch;
  }

  .promo-input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .promo-btn {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 13px 22px;
    min-height: 51px;
    border: 1px solid #ffa800;
    border-radius: 16px;
    background: transparent;
    color: #ffa800;
    font-family: Roboto, Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease;
  }

  .promo-btn:hover:not([disabled]),
  .promo-btn:focus-visible:not([disabled]) {
    background: #ffa800;
    color: #000000;
  }

  .promo-btn[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .promo-clear {
    border-color: rgba(255, 255, 255, 0.4);
    color: rgba(255, 255, 255, 0.7);
  }

  .promo-clear:hover:not([disabled]),
  .promo-clear:focus-visible:not([disabled]) {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }

  .promo-status {
    margin: 0;
    font-size: 13px;
    line-height: 18px;
  }

  .promo-ok {
    color: #ffa800;
  }

  .promo-strike {
    margin-left: 8px;
    color: rgba(255, 255, 255, 0.5);
    text-decoration: line-through;
  }

  .promo-bad {
    color: #ff6b6b;
  }

  @media (max-width: 767px) {
    .promo-group {
      padding: 32px 0 0;
    }

    .promo-row {
      flex-direction: column;
    }

    .promo-btn {
      width: 100%;
      min-height: 44px;
    }
  }

  .help-fallback {
    margin: 24px 0 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    line-height: 20px;
  }

  .help-fallback a {
    color: #ffa800;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .help-fallback a:hover,
  .help-fallback a:focus-visible {
    color: #e7b962;
  }

  .form-actions {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    padding: 48px 30px 0 0;
  }

  .submit {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 13px 23px;
    border: 0;
    border-radius: 5px;
    background: #e7b962;
    color: #000000;
    font-family: Roboto, Arial, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 18px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 160ms ease;
  }

  .submit:hover:not([disabled]),
  .submit:focus-visible:not([disabled]) {
    background: #ffa800;
  }

  .submit[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .arrow {
    width: 8px;
    height: 18px;
    flex: 0 0 8px;
    background: currentColor;
    -webkit-mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
    mask: url('/icons/ui-icon-chevron-right.svg') center / contain no-repeat;
  }

  @media (max-width: 1279px) {
    .registration-section {
      padding: 100px 0;
    }

    .back-row {
      display: none;
    }

    .registration-frame {
      padding: 40px 32px;
    }

    .registration-content {
      width: 100%;
      padding-inline: 0;
    }

    .registration-form {
      width: 100%;
    }

    h1 {
      font-size: 40px;
      line-height: 1.1;
    }

    .registration-note {
      padding: 24px 0 32px;
      font-size: 18px;
      line-height: 28px;
    }

    .agreement {
      font-size: 16px;
    }
  }

  @media (max-width: 1599px) and (min-width: 1280px) {
    h1 {
      font-size: 48px;
      line-height: 1.1;
    }

    .registration-note {
      padding: 24px 30px 40px 0;
      font-size: 20px;
      line-height: 30px;
    }

    .agreement {
      font-size: 16px;
    }
  }

  @media (max-width: 767px) {
    .registration-section {
      padding: 100px 0 40px;
    }

    .registration-frame {
      padding: 0 24px;
    }

    h1 {
      font-size: 32px;
      line-height: 32px;
    }

    .registration-note {
      padding: 24px 0;
      color: rgba(255, 255, 255, 0.7);
      font-size: 18px;
      line-height: normal;
    }

    .agreements {
      padding: 48px 0 0;
    }

    .agreement {
      font-size: 12px;
    }

    .form-actions {
      padding: 48px 0 0;
    }

    .submit {
      width: 100%;
      justify-content: center;
    }
  }
</style>
